import { cn } from '@/lib/cn';
import { TypographyCaption, TypographyP2 } from '@heidi/design-system/typography';
import { ChevronRight, FileText } from 'lucide-react';
import { useCallback, type KeyboardEvent } from 'react';
import { FormattedMessage } from 'react-intl';

interface WorkArtefactCreatedCardProps {
  title: string;
  subtitle?: string;
  /** When omitted, the card is confirmation-only (no open action). */
  onOpen?: () => void;
  className?: string;
}

/** Inline card after `evidence.save_work_output` — opens the library artefact panel. */
export const WorkArtefactCreatedCard = ({
  title,
  subtitle,
  onOpen,
  className,
}: WorkArtefactCreatedCardProps): JSX.Element => {
  const canOpen = typeof onOpen === 'function';

  const handleAction = useCallback(() => {
    onOpen?.();
  }, [onOpen]);

  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLDivElement>) => {
      if (!canOpen) return;
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        handleAction();
      }
    },
    [canOpen, handleAction]
  );

  return (
    <div
      className={cn(
        'relative flex w-full items-center overflow-hidden rounded-lg border border-border bg-surface p-4',
        'transition-all duration-200',
        canOpen &&
          'group cursor-pointer hover:border-border-hover hover:shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-border-focus',
        className
      )}
      data-testid="work-artefact-created-card"
      role={canOpen ? 'button' : undefined}
      tabIndex={canOpen ? 0 : undefined}
      onClick={canOpen ? handleAction : undefined}
      onKeyDown={canOpen ? handleKeyDown : undefined}
    >
      <div className="flex size-8 shrink-0 items-center justify-center rounded-md bg-surface-accent">
        <FileText className="size-4 text-text-primary" />
      </div>
      <div className="ms-3 flex min-w-0 flex-1 flex-col gap-1">
        <TypographyP2 className="line-clamp-2 font-medium text-text-primary">
          {title}
        </TypographyP2>
        {subtitle ? (
          <TypographyCaption className="line-clamp-1 text-text-tertiary">
            {subtitle}
          </TypographyCaption>
        ) : null}
        {canOpen ? (
          <div className="flex w-fit items-center gap-0.5 text-text-secondary group-hover:text-text-primary">
            <TypographyCaption className="text-inherit">
              <FormattedMessage defaultMessage="Open artefact" id="7HrYuNGAiY" />
            </TypographyCaption>
            <ChevronRight className="size-3 rtl:-scale-x-100" />
          </div>
        ) : (
          <TypographyCaption className="text-text-secondary">
            <FormattedMessage defaultMessage="Saved to Library" id="ABJESjnXIB" />
          </TypographyCaption>
        )}
      </div>
    </div>
  );
};
